import {
  MagnifyingGlassIcon,
  NotePencilIcon,
  SidebarSimpleIcon,
} from "@phosphor-icons/react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Button } from "@/components/ui/button";
import ChatHistoryListItem from "@/components/ui/ChatHistoryListItem";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import { IconButton } from "@/components/ui/icon-button";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useResizeHandle } from "@/hooks/useResizeHandle";
import { cn } from "@/lib/utils";
import useHeraldChatHistoryStore from "@/store/herald-chat/herald-chat-history.slice";
import ChatSearchDialog from "./ChatSearchDialog";
import { CHAT_HISTORY_WIDTH_TRANSITION_CLASS } from "./chat-history-sidebar.constants";

const MIN_CHAT_HISTORY_WIDTH = 255;
const COLLAPSED_WIDTH = 55;
const LARGE_SCREEN_BREAKPOINT = 1024;
const DEFAULT_EXPANDED_WIDTH = 300;
const MAX_WIDTH_VIEWPORT_RATIO = 0.25;
const CHAT_HISTORY_LOADING_SKELETON_COUNT = 4;
const CHAT_HISTORY_LOAD_MORE_SKELETON_COUNT = 2;
const CHAT_SEARCH_KEYBOARD_SHORTCUT = "k";

const chatHistoryRouteApi = getRouteApi("/_authenticated/chat/$conversationId");

export interface HeraldChatHistoryProps {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

const HeraldChatHistory = ({
  isExpanded,
  onExpandedChange,
}: HeraldChatHistoryProps) => {
  const navigate = useNavigate();
  const { conversationId: conversationIdParam } =
    chatHistoryRouteApi.useParams();
  const activeConversationId =
    conversationIdParam === "new" ? null : conversationIdParam;
  const allPreviousConversations = useHeraldChatHistoryStore(
    (state) => state.allPreviousConversations,
  );
  const isFetchingConversations = useHeraldChatHistoryStore(
    (state) => state.isFetchingConversations,
  );
  const isFetchingMoreConversations = useHeraldChatHistoryStore(
    (state) => state.isFetchingMoreConversations,
  );
  const hasMoreConversations = useHeraldChatHistoryStore(
    (state) => state.hasMoreConversations,
  );
  const conversationsFetchError = useHeraldChatHistoryStore(
    (state) => state.conversationsFetchError,
  );
  const fetchAllPreviousConversations = useHeraldChatHistoryStore(
    (state) => state.fetchAllPreviousConversations,
  );
  const fetchMorePreviousConversations = useHeraldChatHistoryStore(
    (state) => state.fetchMorePreviousConversations,
  );
  const setRenameConversationTarget = useHeraldChatHistoryStore(
    (state) => state.setRenameConversationTarget,
  );
  const setRenameConversationModal = useHeraldChatHistoryStore(
    (state) => state.setRenameConversationModal,
  );
  const setDeleteConversationModal = useHeraldChatHistoryStore(
    (state) => state.setDeleteConversationModal,
  );
  const targetConversationId = useHeraldChatHistoryStore(
    (state) => state.targetConversationId,
  );
  const isRenameConversationModalOpen = useHeraldChatHistoryStore(
    (state) => state.isRenameConversationModalOpen,
  );
  const isDeleteConversationModalOpen = useHeraldChatHistoryStore(
    (state) => state.isDeleteConversationModalOpen,
  );

  const [width, setWidth] = useState(COLLAPSED_WIDTH);
  const [maxWidth, setMaxWidth] = useState(
    () => window.innerWidth * MAX_WIDTH_VIEWPORT_RATIO,
  );
  const widthRef = useRef(width);
  const isExpandedRef = useRef(isExpanded);
  const prevActiveConversationIdRef = useRef<string | null | undefined>(
    undefined,
  );
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const hasTriggeredLoadMore = useRef(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchKeyboardShortcutLabel = /Mac|iPhone|iPad|iPod/.test(
    navigator.userAgent,
  )
    ? "⌘K"
    : "Ctrl K";

  const setScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    root: scrollRoot,
  });

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const { handleMouseDown, isResizing } = useResizeHandle({
    minWidth: MIN_CHAT_HISTORY_WIDTH,
    maxWidth,
    getCurrentWidth: () => width,
    onWidthChange: setWidth,
  });

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    setWidth((w) => (w <= COLLAPSED_WIDTH ? DEFAULT_EXPANDED_WIDTH : w));
  }, [isExpanded]);

  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
      const currentIsExpanded = isExpandedRef.current;
      const currentWidth = widthRef.current;

      if (!isLargeScreen && currentIsExpanded) {
        setWidth(COLLAPSED_WIDTH);
        onExpandedChange(false);
      }

      const newMaxWidth = window.innerWidth * MAX_WIDTH_VIEWPORT_RATIO;
      setMaxWidth(newMaxWidth);
      if (currentIsExpanded && currentWidth > newMaxWidth) {
        const constrainedWidth = Math.max(
          MIN_CHAT_HISTORY_WIDTH,
          Math.min(newMaxWidth, currentWidth),
        );
        setWidth(constrainedWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [onExpandedChange]);

  const handleNewChat = useCallback(() => {
    void navigate({
      to: "/chat/$conversationId",
      params: { conversationId: "new" },
    });
  }, [navigate]);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== CHAT_SEARCH_KEYBOARD_SHORTCUT ||
        !(event.metaKey || event.ctrlKey)
      ) {
        return;
      }

      event.preventDefault();
      setIsSearchOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Initial list load only. Thread switches use the effect below (network-only) so we
  // do not double-fetch with that path when activeConversationId changes.
  useEffect(() => {
    void fetchAllPreviousConversations({ activeConversationId });
  }, [fetchAllPreviousConversations]);

  /**
   * `network-only` when switching between two saved threads — avoids an extra request on
   * every first-time id assignment (e.g. after stream registration) alongside mount/focus.
   */
  useEffect(() => {
    const prev = prevActiveConversationIdRef.current;
    const next = activeConversationId;
    prevActiveConversationIdRef.current = next;

    if (!next) {
      return;
    }

    const switchedBetweenSavedThreads =
      prev !== undefined && prev !== null && prev !== next;
    if (switchedBetweenSavedThreads) {
      void fetchAllPreviousConversations({
        requestPolicy: "network-only",
        activeConversationId,
      });
    }
  }, [activeConversationId, fetchAllPreviousConversations]);

  useEffect(() => {
    const onFocus = () => {
      void fetchAllPreviousConversations({
        requestPolicy: "network-only",
        activeConversationId,
      });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [activeConversationId, fetchAllPreviousConversations]);

  useEffect(() => {
    if (
      !inView ||
      !hasMoreConversations ||
      isFetchingMoreConversations ||
      isFetchingConversations ||
      hasTriggeredLoadMore.current
    ) {
      return;
    }

    hasTriggeredLoadMore.current = true;
    void fetchMorePreviousConversations().finally(() => {
      hasTriggeredLoadMore.current = false;
    });
  }, [
    inView,
    hasMoreConversations,
    isFetchingMoreConversations,
    isFetchingConversations,
    fetchMorePreviousConversations,
  ]);

  const handleToggle = () => {
    const next = !isExpanded;
    if (isExpanded) {
      setWidth(COLLAPSED_WIDTH);
    } else {
      setWidth(DEFAULT_EXPANDED_WIDTH);
    }
    onExpandedChange(next);
  };

  const renderChatHistory = () => {
    if (conversationsFetchError) {
      return (
        <p className="px-3 text-xxs text-red-600">{conversationsFetchError}</p>
      );
    }
    if (isFetchingConversations && allPreviousConversations.length === 0) {
      return (
        <output className="contents" aria-live="polite">
          <span className="sr-only">Loading conversations</span>
          <ItemSkeleton
            numberOfItems={CHAT_HISTORY_LOADING_SKELETON_COUNT}
            showNestedItems={false}
            containerClassName="space-y-1 px-3"
            groupItemClassName="h-4 w-full rounded-sm"
          />
        </output>
      );
    }
    if (allPreviousConversations.length === 0) {
      return (
        <p className="px-3 text-xxs text-gray-400">No conversations yet</p>
      );
    }
    return (
      <ul className="flex flex-col">
        {allPreviousConversations.map((conv) => (
          <ChatHistoryListItem
            key={conv.id}
            conversationId={conv.id}
            title={conv.title ?? ""}
            isActive={activeConversationId === conv.id}
            onSelect={(id) => {
              void navigate({
                to: "/chat/$conversationId",
                params: { conversationId: id },
              });
            }}
            onRename={(id) => {
              setRenameConversationTarget({
                targetConversationId: id,
                titleToBeRenamed: conv.title?.trim() ?? "",
              });
              setRenameConversationModal({
                open: true,
                conversationId: id,
                title: conv.title ?? "",
              });
            }}
            onDelete={(id) =>
              setDeleteConversationModal({
                open: true,
                conversationId: id,
                title: conv.title ?? "",
              })
            }
            pinOverflowMenu={
              (isRenameConversationModalOpen ||
                isDeleteConversationModalOpen) &&
              targetConversationId === conv.id
            }
          />
        ))}
      </ul>
    );
  };

  const outerWidth = isExpanded ? width : 0;

  return (
    <>
      <div
        className={cn(
          "relative h-full min-h-0 min-w-0 shrink-0",
          isResizing && "select-none",
          CHAT_HISTORY_WIDTH_TRANSITION_CLASS,
        )}
        style={{ width: outerWidth }}
        aria-hidden={!isExpanded}
      >
        <div
          className={cn(
            "relative flex h-full min-h-0 flex-col overflow-hidden border-r border-slate-100 bg-white",
            "scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300",
            !isExpanded && "pointer-events-none",
          )}
        >
          <div className="sticky top-0 z-20 shrink-0 bg-white">
            <div className="flex items-center justify-end px-1.5 py-1">
              <IconButton
                icon={<SidebarSimpleIcon size={16} />}
                variant="ghost"
                size="md"
                onPress={handleToggle}
                aria-label="Collapse history"
                aria-expanded={isExpanded}
                className="z-30 text-gray-600"
                tooltipLabel="Close Sidebar"
                tooltipPosition="right"
                offset={10}
                crossOffset={10}
              />
            </div>
            <div className="flex flex-col gap-1 px-1.5 pb-4 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onPress={handleNewChat}
                aria-label="New chat"
                className="h-8 w-full shrink-0 justify-start gap-1 rounded-md px-1.5 text-xs text-gray-900"
              >
                <NotePencilIcon className="h-3.5 w-3.5" />
                New chat
              </Button>
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={openSearch}
                  aria-label={`Search chats ${searchKeyboardShortcutLabel}`}
                  aria-haspopup="dialog"
                  aria-expanded={isSearchOpen}
                  className="h-8 w-full shrink-0 justify-start gap-1 rounded-md px-1.5 text-xs text-gray-900"
                >
                  <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                  Search
                </Button>
                <Tooltip placement="right" offset={10} crossOffset={10}>
                  {`Search chats ${searchKeyboardShortcutLabel}`}
                </Tooltip>
              </TooltipTrigger>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            <ResizablePanelGroup
              direction="vertical"
              className="min-h-0 flex-1"
            >
              <ResizablePanel defaultSize={35} minSize={15}>
                <div className="flex h-full min-h-0 flex-col">
                  <span className="shrink-0 px-3 text-xxs text-gray-600">
                    Your chats
                  </span>
                  <div
                    ref={setScrollContainerRef}
                    className="min-h-0 flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-150"
                  >
                    <div className="space-y-1 py-2 text-xs text-gray-800">
                      {renderChatHistory()}
                      {isFetchingMoreConversations && (
                        <output className="contents" aria-live="polite">
                          <span className="sr-only">
                            Loading more conversations
                          </span>
                          <ItemSkeleton
                            numberOfItems={
                              CHAT_HISTORY_LOAD_MORE_SKELETON_COUNT
                            }
                            showNestedItems={false}
                            containerClassName="space-y-1 px-3"
                            groupItemClassName="h-4 w-full rounded-sm"
                          />
                        </output>
                      )}
                      {hasMoreConversations &&
                        allPreviousConversations.length > 0 &&
                        !conversationsFetchError && (
                          <div ref={loadMoreRef} className="h-8" />
                        )}
                    </div>
                  </div>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </div>
        <div
          className={cn(
            "hidden md:block",
            !isExpanded && "pointer-events-none",
          )}
        >
          <ResizeHandle
            onMouseDown={handleMouseDown}
            className={cn(
              "z-20",
              isResizing &&
                "after:bg-violet-700 hover:after:bg-violet-700 active:after:bg-violet-700",
            )}
          />
        </div>
      </div>
      <ChatSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

export default HeraldChatHistory;
